import { groupBy, isEmpty, isNil, last, maxBy, sortBy, uniq } from "lodash";
import { FIELD_GROUPS, groupLabel } from "../../data-fields";
import { SECTIONS, zoneSection } from "./zones";
import { searchable } from "utils/helpers/text";

export const SECTION_MODES = {
  LOADING: "loading",
  PDF: "pdf",
  DATA: "data",
};

export const GROUP_KINDS = {
  HEADING: "heading",
  PAGE: "page",
  UNPLACED: "unplaced",
  DATA: "data",
  FLAT: "flat",
};

const UNPLACED_KEY = "unplaced";
const FLAT_KEY = "flat";
const ROW_STEP = 4;
const COLUMN_MARGIN = 0.02;
const BAND_HEIGHTS = 2;
const TOP_SLACK = 2;
const MAX_DISTANCE = 0.6;
const FRAME_SLACK = 1;

const DATA_ORDER = [
  SECTIONS.OTHER,
  ...Object.values(FIELD_GROUPS),
  SECTIONS.SIGNATURE,
];

export const compareReading = (a, b) =>
  a.page - b.page ||
  Math.round(a.top / ROW_STEP) - Math.round(b.top / ROW_STEP) ||
  a.left - b.left;

const overlaps = (a, b) => a.left < b.right && b.left < a.right;

const bandsOf = (headings) =>
  sortBy(headings, "top").reduce((bands, heading) => {
    const band = last(bands);
    const joins =
      !isNil(band) &&
      band.every(
        (other) =>
          Math.abs(other.top - heading.top) <=
            BAND_HEIGHTS * Math.max(other.height, heading.height) &&
          !overlaps(other, heading),
      );
    return joins
      ? [...bands.slice(0, -1), [...band, heading]]
      : [...bands, [heading]];
  }, []);

const withColumns = (headings, width) =>
  bandsOf(headings).flatMap((band) => {
    const sorted = sortBy(band, "left");
    const margin = COLUMN_MARGIN * width;
    return sorted.map((heading, index) => ({
      ...heading,
      from: index === 0 ? 0 : heading.left - margin,
      to: index === sorted.length - 1 ? width : sorted[index + 1].left - margin,
    }));
  });

const pageHeadings = (headings) =>
  groupBy(
    headings.map((heading, index) => ({
      ...heading,
      key: `heading:${heading.page}:${index}`,
    })),
    "page",
  );

const insideFrame = (point, frame) =>
  isNil(frame) ||
  (point.left >= frame.left - FRAME_SLACK &&
    point.left <= frame.right + FRAME_SLACK &&
    point.top >= frame.top - FRAME_SLACK &&
    point.top <= frame.bottom + FRAME_SLACK);

const headingAbove = ({ point, headings, page }) => {
  const eligible = (headings || []).filter(
    (heading) =>
      heading.bottom <= point.top + TOP_SLACK &&
      insideFrame(point, heading.frame),
  );
  if (isEmpty(eligible)) return null;
  const width = page?.width ?? Math.max(...eligible.map(({ right }) => right));
  const closest = maxBy(
    withColumns(eligible, width).filter(
      (heading) => point.left >= heading.from && point.left < heading.to,
    ),
    "top",
  );
  if (isNil(closest)) return null;
  return point.top - closest.bottom > MAX_DISTANCE * (page?.height ?? Infinity)
    ? null
    : closest;
};

const sameTitle = (left, right) =>
  left.kind === right.kind &&
  left.page === right.page &&
  searchable(left.title) === searchable(right.title);

const mergeNeighbours = (groups) =>
  groups.reduce((merged, group) => {
    const previous = last(merged);
    if (isNil(previous) || !sameTitle(previous, group))
      return [...merged, group];
    return [
      ...merged.slice(0, -1),
      { ...previous, zoneIds: [...previous.zoneIds, ...group.zoneIds] },
    ];
  }, []);

const placeOfZone = (zoneBoxes) => {
  const [first] = [...zoneBoxes].sort(compareReading);
  return {
    first,
    places: Math.max(
      ...Object.values(groupBy(zoneBoxes, "anchor")).map(
        (sameAnchor) => uniq(sameAnchor.map(({ marker }) => marker)).length,
      ),
    ),
    pages: sortBy(uniq(zoneBoxes.map(({ page }) => page))),
  };
};

export const pdfSections = ({
  zones,
  boxes,
  headings = [],
  pages = [],
  labelOf = (zone) => zone.id,
}) => {
  if (isEmpty(boxes)) return null;
  const byPage = pageHeadings(headings);
  const located = [];
  const unplaced = [];
  for (const zone of zones) {
    const zoneBoxes = boxes[zone.id];
    if (isEmpty(zoneBoxes)) {
      unplaced.push(zone);
      continue;
    }
    const { first, places, pages: zonePages } = placeOfZone(zoneBoxes);
    const heading = headingAbove({
      point: first,
      headings: byPage[first.page],
      page: pages[first.page],
    });
    located.push({ zone, first, places, zonePages, heading });
  }

  const groups = [];
  const byKey = new Map();
  for (const { zone, first, heading } of [...located].sort((a, b) =>
    compareReading(a.first, b.first),
  )) {
    const key = isNil(heading) ? `page:${first.page}` : heading.key;
    if (!byKey.has(key)) {
      const group = {
        key,
        kind: isNil(heading) ? GROUP_KINDS.PAGE : GROUP_KINDS.HEADING,
        title: heading?.text ?? null,
        page: first.page,
        zoneIds: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).zoneIds.push(zone.id);
  }

  const merged = mergeNeighbours(groups);
  const all = isEmpty(unplaced)
    ? merged
    : [
        ...merged,
        {
          key: UNPLACED_KEY,
          kind: GROUP_KINDS.UNPLACED,
          title: null,
          page: null,
          zoneIds: [...unplaced]
            .sort((a, b) => labelOf(a).localeCompare(labelOf(b), "fr"))
            .map((zone) => zone.id),
        },
      ];

  const groupOf = new Map(
    all.flatMap((group) => group.zoneIds.map((id) => [id, group.key])),
  );
  const placeOf = new Map(
    located.map(({ zone, first, places, zonePages }) => [
      zone.id,
      {
        key: groupOf.get(zone.id),
        page: first.page,
        top: first.top,
        left: first.left,
        places,
        pages: zonePages,
      },
    ]),
  );
  return { groups: all, placeOf };
};

const groupTitle = (group, t) => {
  if (group.kind === GROUP_KINDS.PAGE)
    return t("ZONES.PAGE", { page: group.page + 1 });
  if (group.kind === GROUP_KINDS.UNPLACED) return t("ZONES.UNPLACED");
  return group.title;
};

const dataGroups = ({ zones, mappings, t }) => {
  const bySection = groupBy(zones, (zone) => zoneSection(zone, mappings));
  return DATA_ORDER.filter((section) => !isEmpty(bySection[section])).map(
    (section) => {
      const title = Object.values(FIELD_GROUPS).includes(section)
        ? groupLabel(section)
        : t(`ZONES.SECTIONS.${section}`);
      return {
        key: `data:${section}`,
        kind: GROUP_KINDS.DATA,
        title,
        context: title,
        page: null,
        zoneIds: bySection[section].map((zone) => zone.id),
      };
    },
  );
};

export const listGroups = ({ sections, zones, mappings, t }) => {
  if (sections.mode === SECTION_MODES.LOADING)
    return [
      {
        key: FLAT_KEY,
        kind: GROUP_KINDS.FLAT,
        title: null,
        context: null,
        page: null,
        zoneIds: zones.map((zone) => zone.id),
      },
    ];
  if (sections.mode === SECTION_MODES.DATA)
    return dataGroups({ zones, mappings, t });
  return sections.groups.map((group) => ({
    ...group,
    title: groupTitle(group, t),
    context: group.kind === GROUP_KINDS.HEADING ? group.title : null,
  }));
};
